<!-- Thank you: https://github.com/livelybone/vuejs-snackbar/blob/master/src/components/Snackbar.vue -->

<template>
  <div class="snack-bar-wrapper" :class="wrapClass" :style="wrapClass ? '' : style.wrap">
    <div v-for="(snack, idx) in snacks" :key="idx + 's'" aria-live="assertive" aria-atomic="true">
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="snack-bar"
        :style="wrapClass ? '' : style.bar(snack.color)"
        @click="pop(snack.id)"
        v-html="snack.message"
      ></div>
      <!-- eslint-enable vue/no-v-html -->
      <br />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

defineOptions({ name: "SnackbarComponent" });

interface Props {
  colors?: Record<string, string>;
  openTime?: number;
  multiple?: boolean;
  wrapClass?: string;
  baseSize?: string;
  position?: string;
}

const props = withDefaults(defineProps<Props>(), {
  colors: () => ({
    error: "red",
    warning: "orange",
    info: "green"
  }),
  openTime: 3000,
  multiple: true,
  wrapClass: "",
  baseSize: "100px",
  position: "bottom-center"
});

const getStyle = (baseSize, position) => {
  const c = f => `calc(${f} * ${baseSize})`;
  const { pos, textAlign } = position;
  return {
    wrap: {
      position: "fixed",
      left: 0,
      [pos]: c(0.05),
      zIndex: 1000,
      width: "100%",
      padding: `0 ${c(0.2)}`,
      pointerEvents: "none",
      textAlign
    },
    bar: bg => ({
      display: "inline-block",
      width: "auto",
      minWidth: baseSize,
      maxWidth: `calc(100vw - ${c(0.4)})`,
      padding: `${c(0.15)} ${c(0.2)}`,
      margin: `0 0 ${c(0.05)}`,
      borderRadius: c(0.02),
      lineHeight: c(0.2),
      color: "#fff",
      background: bg,
      boxShadow: `0 ${c(0.01)} ${c(0.025)} rgba(0,0,0, .15)`,
      cursor: "pointer",
      textAlign: "center",
      pointerEvents: "all",
      userSelect: "none"
    })
  };
};

const snacks = ref<any[]>([]);

const _baseSize = computed(() => (/\d(rem|px|em)$/.test(props.baseSize) ? props.baseSize : "100px"));

const _position = computed(() => {
  const [p, textAlign] = props.position.toString().split("-");
  return {
    pos: ["top", "bottom"].includes(p) ? p : "top",
    textAlign: ["left", "center", "right"].includes(textAlign) ? textAlign : "center"
  };
});

const style = computed(() => getStyle(_baseSize.value, _position.value));

const remove = id => {
  const i = snacks.value.findIndex(snack => snack.id === id);
  if (i > -1) {
    clearTimeout(snacks.value[i].timer);
    snacks.value.splice(i, 1);
  }
};

const add = snack => {
  const snackObj: any = {
    id: Math.floor(Math.random() * 100000),
    message: snack.message,
    type: snack.type,
    color: props.colors[snack.type],
    keepOpen: snack.keepOpen
  };
  if (!snack.keepOpen) {
    snackObj.timer = setTimeout(() => {
      remove(snackObj.id);
    }, props.openTime);
  }

  if (props.multiple) {
    snacks.value.push(snackObj);
  } else {
    snacks.value = [snackObj];
  }

  return snackObj.id;
};

const addInfo = (message, keepOpen) => add({ message, type: "info", keepOpen });
const addWarning = (message, keepOpen) => add({ message, type: "warning", keepOpen });
const addError = (message, keepOpen) => add({ message, type: "error", keepOpen });

const update = (id, message) => {
  const snack = snacks.value.find(snack => snack.id === id);
  if (snack) {
    snack.message = message;
  }
};

const pop = id => {
  if (!snacks.value.find(snack => snack.id === id).keepOpen) {
    remove(id);
  }
};

defineExpose({ addInfo, addWarning, addError, update, remove, pop });
</script>
